import React from 'react'
import { FileText, Trash2 } from 'lucide-react'
import { Button } from '@iws/admin-ui/button'
import { MediaFile } from '../types'

const IMAGE_EXT = /\.(avif|bmp|gif|ico|jpe?g|png|svg|webp)$/i

interface MediaSidebarProps {
  selectedFile: MediaFile
  resolveUrl: (path: string) => string
  formatSize: (bytes: number) => string
  onConfirm?: () => void
  onDelete?: () => void
  onOptimize?: () => void
  onConvert?: () => void
}

export const MediaSidebar = ({
  selectedFile,
  resolveUrl,
  formatSize,
  onConfirm,
  onDelete,
  onOptimize,
  onConvert,
}: MediaSidebarProps) => {
  const isImage = selectedFile.type.includes('image') || IMAGE_EXT.test(selectedFile.name)
  const extension = (selectedFile.name.split('.').pop() ?? selectedFile.type.split('/')[1] ?? '').toUpperCase()
  const details: [string, string][] = [
    ['Type', extension],
    ['Size', formatSize(selectedFile.size)],
  ]
  if (selectedFile.width && selectedFile.height) {
    details.push(['Dimensions', `${selectedFile.width} × ${selectedFile.height}`])
  }

  return (
    <aside className="hidden w-80 shrink-0 flex-col border-l border-border-subtle bg-card lg:flex">
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
        <div className="grid aspect-[4/3] place-items-center overflow-hidden rounded-lg border border-border-subtle bg-subdued">
          {isImage ? (
            <img
              src={resolveUrl(selectedFile.path)}
              className="max-h-full max-w-full object-contain"
              alt={selectedFile.name}
            />
          ) : (
            <FileText className="size-12 text-muted-foreground" />
          )}
        </div>

        <p className="break-all text-sm font-semibold text-ink">{selectedFile.name}</p>

        <dl className="divide-y divide-border-subtle rounded-lg border border-border-subtle">
          {details.map(([label, value]) => (
            <div key={label} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
              <dt className="text-text-2">{label}</dt>
              <dd className="tabular-nums text-ink">{value}</dd>
            </div>
          ))}
        </dl>

        {(onConfirm || onOptimize || onConvert) && (
          <div className="space-y-2">
            {onConfirm && (
              <Button className="w-full" onClick={onConfirm}>
                Use file
              </Button>
            )}
            {onOptimize && (
              <Button variant="outline" className="w-full" onClick={onOptimize}>
                Optimize
              </Button>
            )}
            {onConvert && (
              <Button variant="outline" className="w-full" onClick={onConvert}>
                Convert
              </Button>
            )}
          </div>
        )}
      </div>

      {onDelete && (
        <div className="border-t border-border-subtle p-3">
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-error hover:bg-error-lighter hover:text-error-dark"
            onClick={onDelete}
          >
            <Trash2 /> Delete file
          </Button>
        </div>
      )}
    </aside>
  )
}
